/**
 * versionPane — the studio VERSIONS list, lifted out of WidgetEditor so more
 * than one studio surface can operate the SAME real version ladder.
 *
 * Consumers: `WidgetEditor` (the HTML-bundle studio) and
 * `widget-native/studio` (the ViewIR studio). Both read
 * `widgetApi.listVersionLabels(id)` and both publish/unpublish through
 * `widgetApi.publish` / `widgetApi.clearTag(id, 'release')` — there is exactly
 * one pin (PUBLIC = `widget.tags.release`) and one place that renders it.
 *
 * Nothing here fetches: the caller owns loading + error state and passes
 * `versionList` / `versionsError` down, so a surface that has no widget row in
 * this environment can render an honest empty state instead of a spinner.
 */
import { TAG_LABELS } from './tagLabels';

// Light palette — the subset the version UI needs, matching WidgetEditor's
// COLORS map so a lifted row looks identical in both studios.
const COLORS = {
  border: '#d8d8dc',
  text: '#1d1d1f',
  textDim: '#6e6e73',
  accent: '#5856d6',
  accentDim: '#eeedf5',
  warn: '#ff9500',
  release: '#34c759',
  dev: '#6e6e73',
};

export function TagDot({ tag, version }) {
  if (!version) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 6px', borderRadius: 4,
      background: `${COLORS[tag]}22`, color: COLORS[tag],
      fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: COLORS[tag] }} />
      {TAG_LABELS[tag] || tag}: {version}
    </span>
  );
}

// Parse a semver-ish app version ("0.8.1", "0.8.1+4", "0.8") to a 3-int tuple;
// build/pre suffixes and non-numeric parts → 0. Mirrors the server's _semver_key.
function appVerKey(v) {
  if (!v) return [0, 0, 0];
  const core = String(v).trim().split(/[+-]/)[0];
  const parts = core.split('.').slice(0, 3).map((p) => {
    const n = parseInt(p, 10);
    return Number.isFinite(n) ? n : 0;
  });
  while (parts.length < 3) parts.push(0);
  return parts;
}

function cmpAppVer(a, b) {
  const ka = appVerKey(a);
  const kb = appVerKey(b);
  for (let i = 0; i < 3; i++) {
    if (ka[i] !== kb[i]) return ka[i] < kb[i] ? -1 : 1;
  }
  return 0;
}

// One badge on a VERSIONS-list row — the PUBLIC pin (release) in green. This
// is the only pin in the studio now (Private/Scratch were retired).
export function VersionBadge({ kind = 'public' }) {
  const color = COLORS.release;
  return (
    <span style={{
      padding: '1px 5px', borderRadius: 4,
      background: `${color}22`, color,
      fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>
      {kind}
    </span>
  );
}

// One row of the VERSIONS list (v96 merged local + cross-env shared).
// Click / double-click selects the version — the preview + read-only file
// pane repaint to that exact version. The PUBLIC pin wears the green badge.
// There is NO delete affordance here by design — version deletion is
// /widget-drive-only. The version label renders in FULL (wraps; never
// ellipsized) so same-prefix labels stay distinguishable.
export function VersionRow({ entry, selected, onSelect, boundaryMin }) {
  return (
    <div
      onClick={() => onSelect?.(entry)}
      onDoubleClick={() => onSelect?.(entry)}
      title={entry.public ? 'Public version. Click to view it.' : 'Click to view this version.'}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px',
        borderRadius: 6, cursor: 'pointer', userSelect: 'none',
        background: selected ? COLORS.accentDim : 'transparent',
      }}
    >
      <span style={{
        flex: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-all',
        fontSize: 11, lineHeight: 1.4,
        color: selected ? COLORS.accent : COLORS.text,
        fontWeight: selected ? 700 : 400,
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
      }}>
        {entry.version_label}
      </span>
      {entry.public && <VersionBadge kind="public" />}
      {!entry.local && entry.shared && (
        <span title={`Committed on ${entry.origin_env || 'another environment'} — not adopted here yet`}
              style={{ fontSize: 9, color: COLORS.textDim }}>
          {entry.origin_env || 'shared'}
        </span>
      )}
      {boundaryMin ? (
        // This is the newest version an app at `boundaryMin` can still render —
        // the server serves it to those older apps (v133 compat resolver).
        <span title={`Apps on ${boundaryMin} are served this version (the newest one they support)`}
              style={{
                padding: '1px 5px', borderRadius: 4,
                background: `${COLORS.warn}22`, color: COLORS.warn,
                fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap',
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              }}>
          ≥{boundaryMin}
        </span>
      ) : entry.app_min_version ? (
        <span title="Minimum app version required to render this widget version"
              style={{
                fontSize: 9, color: COLORS.textDim, whiteSpace: 'nowrap',
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              }}>
          ≥{entry.app_min_version}
        </span>
      ) : null}
    </div>
  );
}

export function VersionPane({ tags, versionList, versionsError, selectedVersion, onSelectVersion, emptyHint }) {
  const entries = versionList?.versions || [];
  // Compat boundary badges (v133): for each distinct app_min_version LOWER than the
  // latest version's minimum, mark the NEWEST version carrying it — that's the version
  // an app at that tier is served (the newest one it supports). entries are newest-first.
  const boundaryByLabel = {};
  const latestMin = entries[0]?.app_min_version || null;
  if (latestMin) {
    const seen = new Set();
    for (const e of entries) {
      const m = e.app_min_version;
      if (!m || seen.has(m)) continue;
      if (cmpAppVer(m, latestMin) < 0) {
        boundaryByLabel[e.version_label] = m;
        seen.add(m);
      }
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 8px' }}>
      <div style={{ fontSize: 10, color: COLORS.textDim, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Versions
      </div>
      <TagDot tag="release" version={tags?.release?.version_label} />
      {entries.length === 0 && (
        <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>
          {versionsError
            // Fetch failed (or is still failing) — do NOT claim the widget has no
            // versions; that misled a reader into thinking uploads were lost.
            ? (emptyHint || 'Couldn’t load versions. Tap Refresh to retry.')
            : versionList === null
              // Still loading — the fetch hasn’t resolved yet.
              ? 'Loading versions…'
              // Genuinely empty: the list loaded and carries zero versions.
              : 'No versions yet. Author and upload a version via /widget-drive.'}
        </div>
      )}
      {entries.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2 }}>
          {entries.map((entry) => (
            <VersionRow
              key={entry.version_label}
              entry={entry}
              selected={entry.version_label === selectedVersion}
              onSelect={onSelectVersion}
              boundaryMin={boundaryByLabel[entry.version_label]}
            />
          ))}
        </div>
      )}
      {versionList?.shared_key && (
        <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 2 }}
             title={`Shared VERSIONS list — every environment linked to key ${versionList.shared_key} sees these versions with its own Private/Public badges.`}>
          shared across environments
        </div>
      )}
    </div>
  );
}
